/** @jsxImportSource @emotion/react */
import { faMinus, faTemperatureHigh } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LAYER_TYPE } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useRemoveLayerChildMutation } from "../../../../hooks/mutations/useLayerMutations";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import { Button } from "../../../Helper/Button";
import { Loading } from "../../../Helper/Loading";
import AvosModal from "../../Helper/Modal";
import { MetaRow } from "./LayerMeta";


import { useRouteLayer, useRouteLayerChildren, useRouteLayerQuery } from "../../../../hooks/useCurrentEntities";

export default function LayerParentsChildren() {
    const params = useParams();
    const isLoading = useRouteLayerQuery().isLoading;
    const layer = useRouteLayer();
    const { children, parents } = layer;
    const childrenWithMeta = useRouteLayerChildren();
    const tenantId = params.tenant_id as string;
    const queryClient = useQueryClient();

    const [parentModal, setParentModal] = useState(false);
    const [childrenModal, setChildrenModal] = useState(false);

    const layerWithTempSensors = childrenWithMeta?.results?.filter(
        (i:any) => i.temptale_number && i?.temptale_number !== "None" && i?.temptale_number !== "-" && i?.temptale_number !== "0"
    ).map((i) => i.label) || [];

    const config = useConfig();
    const displayLayerType = (type: string) => config.get_raw_layer_config(type)?.text || type;
    const removeLayerChildMutation = useRemoveLayerChildMutation();

    const layerConfig = config.get_layer_config(layer);
    const { hide_sub_pallets } = layerConfig;

    if (isLoading) {
        return <Loading />;
    }

    if (!isFilledArray(children) && !isFilledArray(parents)) {
        return null;
    }

    const sortedChildren = getFilledArrayOrDefault(children)
        .filter(({ type }) => (!hide_sub_pallets || type !== LAYER_TYPE.SUBPALLET))
        .sort((a, b) => a.label.localeCompare(b.label));

    const onDeleteRelation = (parentId: string, layerId: string) => {
        removeLayerChildMutation.mutateAsync({ layer: { tenant_id: tenantId, id: parentId }, childLayerId: layerId })
            .then(() => {
                queryClient.invalidateQueries({ queryKey: ["layer", tenantId, params.layer_id] });
                queryClient.invalidateQueries({ queryKey: ["layerChildren", tenantId, params.layer_id] });
            });
    };

    return (
        <div className="pt-4">
            {isFilledArray(parents) && (
                <div className="cursor-pointer" onClick={() => setParentModal(true)}>
                    {parents.map((parent) => (
                        <MetaRow key={parent.id} label={displayLayerType(parent.type)} value={<Link to={`/layer/${parent.id}`}>{parent.label}</Link>} />
                    ))}
                </div>
            )}

            <AvosModal
                header={`Linked ${(displayLayerType(layerConfig.parent_layer_type as string) || "items")}`}
                onClose={() => setParentModal(false)}
                size="md"
                showSaveButton={false}
                isOpen={parentModal && !!parents?.length}
            >
                {parents.map((parent) => (
                    <RelationRow
                        key={parent.id}
                        type={displayLayerType(parent.type)}
                        onDelete={() => onDeleteRelation(parent.id, layer.id)}
                    >
                        <Link to={`/layer/${parent.id}`}>{parent.label}</Link>
                    </RelationRow>
                ))}
            </AvosModal>

            {sortedChildren.length > 0 && (
                <div className="cursor-pointer" onClick={() => setChildrenModal(true)}>
                    {sortedChildren.map(({ id, type, label }) => {
                        const hasTempSensor = layerWithTempSensors.includes(label);
                        return (
                            <MetaRow
                                key={id}
                                label={
                                    <div>
                                        {displayLayerType(type)}{" "}
                                        {hasTempSensor && (
                                            <span className="me-2">
                                                <FontAwesomeIcon icon={faTemperatureHigh} />
                                            </span>
                                        )}
                                    </div>
                                }
                                value={<Link to={`/layer/${id}`}>{label}</Link>}
                            />
                        );
                    })}
                </div>
            )}

            <AvosModal
                header={`Linked ${(displayLayerType(layerConfig.children_layer_type as string) || "items")}`}
                onClose={() => setChildrenModal(false)}
                size="md"
                showSaveButton={false}
                isOpen={childrenModal && !!sortedChildren?.length}
            >
                {sortedChildren.map(({ id, type, label }) => {
                    const hasTempSensor = layerWithTempSensors.includes(label);
                    return (
                        <RelationRow
                            key={id}
                            type={displayLayerType(type)}
                            onDelete={() => onDeleteRelation(layer.id, id)}
                        >
                            <Link to={`/layer/${id}`}>{label}</Link>
                            {hasTempSensor && (
                                <span className="me-2">
                                    <FontAwesomeIcon icon={faTemperatureHigh} />
                                </span>
                            )}
                        </RelationRow>
                    );
                })}
            </AvosModal>
        </div>
    );
}

interface RelationRowProps {
    children: React.ReactNode;
    type: string;
    onDelete: () => void;
}

function RelationRow({ children, type, onDelete }: RelationRowProps) {
    return (
        <div className="d-sm-flex align-items-center py-1">
            <div className="pe-2">
                <Button
                    onClick={onDelete}
                    color="danger"
                    outline
                    size="sm"
                    icon={faMinus}
                />
            </div>

            <div className="ms-auto">{type} {children}</div>
        </div>
    );
}
